import {
  CONTRACT_TYPES,
  CONTRACTING_ENTITIES,
  deriveRenewalNoticeDeadline,
  type ExtractedFields,
  type VisibleContract,
} from "@contract-manager/shared";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import { type ApiClient, ApiError } from "../api/client";
import {
  type ApprovalFormField,
  type ApprovalFormState,
  type AutoRenewalAnswer,
  fromExtracted,
  toApprovalPayload,
} from "../approvalForm";
import { useAuth } from "../auth/AuthContext";
import { describeError, isSignedOutError } from "../auth/authErrors";
import { useI18n } from "../i18n/I18n";
import {
  CONTRACT_TYPE_LABEL,
  ENTITY_LABEL,
  readTrackerLabel,
} from "../tracker";
import { Alert } from "./Alert";

/**
 * The extracted fields as inputs, for the person accepting them.
 *
 * Shown in place of the read-only list while a contract sits at `EXTRACTED`
 * and the reader may edit it. Inline rather than in a dialog: there are a
 * dozen fields, and the reviewer has the PDF open beside them.
 */

/** Two people opening the same contract is the ordinary case, not a fault. */
function isConflict(caught: unknown): boolean {
  return (
    caught instanceof ApiError &&
    caught.detail?.code === "CONTRACT_NOT_REVIEWABLE"
  );
}

/** What the API will store as the deadline, if the form is sent as it is. */
function previewDeadline(state: ApprovalFormState): string | null {
  const endDate = state.endDate.trim();
  const daysText = state.renewalNoticePeriodDays.trim();
  if (endDate === "" || daysText === "") {
    return null;
  }
  const days = Number(daysText);
  return Number.isInteger(days) && days > 0
    ? deriveRenewalNoticeDeadline(endDate, days)
    : null;
}

function FormField({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: ReactNode;
}) {
  return (
    <div className="ds-field">
      <label className="ds-text --label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
    </div>
  );
}

export function ApprovalForm({
  api,
  contractId,
  extracted,
  onApproved,
  onConflict,
}: {
  api: ApiClient;
  contractId: string;
  extracted: ExtractedFields;
  onApproved: (contract: VisibleContract) => void;
  /** Somebody got there first; the page reloads to show who. */
  onConflict: (cause: unknown) => void;
}) {
  const auth = useAuth();
  const { t } = useI18n();
  const [state, setState] = useState(() => fromExtracted(extracted));
  const [invalid, setInvalid] = useState<ReadonlySet<ApprovalFormField>>(
    () => new Set(),
  );
  const [failure, setFailure] = useState<{ cause: unknown } | null>(null);
  const [saving, setSaving] = useState(false);
  const formId = useId();
  const idOf = (field: ApprovalFormField): string => `${formId}-${field}`;

  /** The props every control shares: id, value, handler, and error state. */
  const control = (field: ApprovalFormField, className: string) => ({
    id: idOf(field),
    className: invalid.has(field) ? `${className} --alert` : className,
    value: state[field],
    onChange: (event: { target: { value: string } }) =>
      setState((current) => ({ ...current, [field]: event.target.value })),
    "aria-invalid": invalid.has(field) ? true : undefined,
    disabled: saving,
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const payload = toApprovalPayload(state);
    if (!payload.ok) {
      setInvalid(new Set(payload.invalid));
      return;
    }
    setInvalid(new Set());
    setFailure(null);
    setSaving(true);
    try {
      onApproved(await api.approveContract(contractId, payload.value));
    } catch (caught) {
      if (isSignedOutError(caught)) {
        auth.reportSignedOut();
        return;
      }
      if (isConflict(caught)) {
        onConflict(caught);
        return;
      }
      setFailure({ cause: caught });
    } finally {
      setSaving(false);
    }
  };

  const deadline = previewDeadline(state);

  return (
    <form
      className="ds-rows --gap-small"
      noValidate
      onSubmit={(event) => void submit(event)}
    >
      <Alert tone="warning" title={t("contracts.review.title")}>
        {t("contracts.review.body")}
      </Alert>

      {invalid.size > 0 && (
        <Alert tone="failed">{t("contracts.review.invalid")}</Alert>
      )}
      {failure !== null && (
        <Alert tone="failed" title={t("contracts.review.failed")}>
          {describeError(failure.cause, t)}
        </Alert>
      )}

      <FormField
        label={t("contracts.detail.field.title")}
        htmlFor={idOf("title")}
      >
        <input {...control("title", "ds-input")} />
      </FormField>
      <FormField
        label={t("contracts.detail.field.type")}
        htmlFor={idOf("contractType")}
      >
        <select {...control("contractType", "ds-select")}>
          {CONTRACT_TYPES.map((type) => (
            <option key={type} value={type}>
              {readTrackerLabel(CONTRACT_TYPE_LABEL[type], t)}
            </option>
          ))}
        </select>
      </FormField>
      <FormField
        label={t("contracts.detail.field.counterparty")}
        htmlFor={idOf("counterparty")}
      >
        <input {...control("counterparty", "ds-input")} />
      </FormField>
      <FormField
        label={t("contracts.detail.field.shortName")}
        htmlFor={idOf("counterpartyShortName")}
      >
        <input {...control("counterpartyShortName", "ds-input")} />
      </FormField>
      <FormField
        label={t("contracts.detail.field.entity")}
        htmlFor={idOf("contractingEntity")}
      >
        <select {...control("contractingEntity", "ds-select")}>
          {CONTRACTING_ENTITIES.map((entity) => (
            <option key={entity} value={entity}>
              {readTrackerLabel(ENTITY_LABEL[entity], t)}
            </option>
          ))}
        </select>
      </FormField>
      {/* ds-rows turns a form into one column (docs: SDS pitfalls); ds-cols
          puts paired inputs back on one line. */}
      <div className="ds-cols --gap-small">
        <FormField
          label={t("contracts.detail.field.amount")}
          htmlFor={idOf("amountValue")}
        >
          <input {...control("amountValue", "ds-input")} inputMode="decimal" />
        </FormField>
        <FormField
          label={t("contracts.review.currency")}
          htmlFor={idOf("amountCurrency")}
        >
          <input {...control("amountCurrency", "ds-input")} maxLength={3} />
        </FormField>
      </div>
      <div className="ds-cols --gap-small">
        <FormField
          label={t("contracts.detail.field.effectiveDate")}
          htmlFor={idOf("effectiveDate")}
        >
          <input {...control("effectiveDate", "ds-input")} type="date" />
        </FormField>
        <FormField
          label={t("contracts.detail.field.endDate")}
          htmlFor={idOf("endDate")}
        >
          <input {...control("endDate", "ds-input")} type="date" />
        </FormField>
      </div>
      <FormField
        label={t("contracts.detail.field.autoRenewal")}
        htmlFor={idOf("autoRenewal")}
      >
        <select {...control("autoRenewal", "ds-select")}>
          <option value={"" satisfies AutoRenewalAnswer}>
            {t("contracts.detail.notStated")}
          </option>
          <option value={"yes" satisfies AutoRenewalAnswer}>
            {t("contracts.detail.yes")}
          </option>
          <option value={"no" satisfies AutoRenewalAnswer}>
            {t("contracts.detail.no")}
          </option>
        </select>
      </FormField>
      <div className="ds-cols --gap-small">
        <FormField
          label={t("contracts.review.renewalTermMonths")}
          htmlFor={idOf("renewalTermMonths")}
        >
          <input
            {...control("renewalTermMonths", "ds-input")}
            inputMode="numeric"
          />
        </FormField>
        <FormField
          label={t("contracts.review.noticePeriodDays")}
          htmlFor={idOf("renewalNoticePeriodDays")}
        >
          <input
            {...control("renewalNoticePeriodDays", "ds-input")}
            inputMode="numeric"
          />
        </FormField>
      </div>
      <p className="ds-text --small --muted">
        {t("contracts.review.derivedDeadline", {
          deadline: deadline ?? t("contracts.detail.notStated"),
        })}
      </p>

      <div>
        <button
          type="submit"
          className={`ds-button --primary --icon-check${saving ? " --loading" : ""}`}
          disabled={saving}
          // SDS hides the label of a loading button; keep its name.
          aria-label={saving ? t("contracts.review.submitting") : undefined}
        >
          <span>
            {saving
              ? t("contracts.review.submitting")
              : t("contracts.review.submit")}
          </span>
        </button>
      </div>
    </form>
  );
}
